import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Leaf, Truck, ShieldCheck } from "lucide-react";
import { IMG, scrollToId } from "@/lib/api";
import { useStore } from "@/lib/store";
import { StockPill } from "@/components/site/Reveal";

const LINES = [
  [{ t: "Press slow." }],
  [{ t: "Pour ", i: false }, { t: "golden", i: true }],
  [{ t: "every hour." }],
];
const ease = [0.22, 1, 0.36, 1];

const MaskedLine = ({ parts, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.25 + i * 0.14, ease }}>
      {parts.map((p, k) => (
        <span key={k} className={p.i ? "italic text-[#C85A32]" : ""}>{p.t}</span>
      ))}
    </motion.span>
  </span>
);

const Badge = () => (
  <div className="pointer-events-none absolute -left-8 bottom-16 z-20 h-32 w-32 sm:-left-12 sm:h-36 sm:w-36">
    <svg viewBox="0 0 200 200" className="spin-slow h-full w-full">
      <defs><path id="c" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
      <circle cx="100" cy="100" r="98" fill="#181615" />
      <text fill="#F2EAE1" fontSize="15" letterSpacing="5" fontFamily="JetBrains Mono">
        <textPath href="#c">COLD PRESSED · SLOW JUICED · GOLDEN HOUR ·</textPath>
      </text>
    </svg>
    <span className="font-display absolute inset-0 flex items-center justify-center text-2xl italic text-[#E0A030]">01</span>
  </div>
);

const HeroImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} className="relative mx-auto w-full max-w-[520px]" style={{ perspective: 1200 }}>
      <motion.div
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 28px 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 28px 28px)" }}
        transition={{ duration: 1.4, delay: 0.35, ease }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative aspect-[4/5] overflow-hidden rounded-t-full rounded-b-[28px] bg-[#E8DCCD] shadow-[0_40px_80px_-30px_rgba(120,60,20,0.45)]"
      >
        <motion.img data-testid="hero-product-image" src={IMG.studio} alt="The Slowpour 01 juicer in golden hour light" style={{ y, scale: 1.18 }} className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#181615]/35 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between text-[#FDFBF7]">
          <span className="eyebrow">The Slowpour 01</span>
          <span className="font-display text-3xl">$250</span>
        </div>
      </motion.div>
      <Badge />
    </div>
  );
};

export const Hero = () => {
  const { stock, initial, openCheckout } = useStore();
  return (
    <section data-testid="hero-section" className="grain relative overflow-hidden bg-[#F2EAE1] pt-[104px]">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[620px] w-[620px] rounded-full bg-[#E0A030]/25 blur-[120px]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-14 px-5 pb-20 pt-8 sm:px-8 lg:grid-cols-12 lg:pb-28 lg:pt-14">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }} className="mb-8 flex flex-wrap items-center gap-3">
            <span className="eyebrow text-[#C85A32]">Self-feeding cold press</span>
            <StockPill stock={stock} initial={initial} testId="hero-inventory-badge" />
          </motion.div>
          <h1 data-testid="hero-heading" className="font-display text-[3.2rem] font-light leading-[0.95] tracking-[-0.03em] sm:text-7xl lg:text-[6.4rem]">
            {LINES.map((parts, i) => <MaskedLine key={i} parts={parts} i={i} />)}
          </h1>
          <motion.p data-testid="hero-subheading" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9, ease }} className="mt-8 max-w-xl text-base leading-relaxed text-[#4A4543] md:text-lg">
            The Slowpour 01 brings the juice bar home. Drop in whole fruit, close the lid, and let a slow masticating auger press every last drop — no foam, no heat, no fuss.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease }} className="mt-10 flex flex-wrap items-center gap-4">
            <button data-testid="hero-buy-now-button" onClick={openCheckout} className="group inline-flex items-center gap-3 rounded-full bg-[#C85A32] px-8 py-4 font-semibold text-[#FDFBF7] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#A84422]">
              Buy now — $250
              <ArrowDownRight size={18} className="transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <button data-testid="hero-3d-trigger-button" onClick={() => scrollToId("studio")} className="rounded-full border border-[#181615]/20 px-7 py-4 font-medium transition-[background-color,color] duration-300 hover:bg-[#181615] hover:text-[#FDFBF7]">
              Explore in 3D
            </button>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 1 }} className="mt-12 grid max-w-xl grid-cols-1 gap-4 text-sm text-[#4A4543] sm:grid-cols-3">
            <li data-testid="hero-trust-badge-bpa" className="flex items-center gap-2"><Leaf size={16} className="text-[#5E7153]" /> BPA-free build</li>
            <li data-testid="hero-trust-badge-shipping" className="flex items-center gap-2"><Truck size={16} className="text-[#5E7153]" /> $15 flat shipping</li>
            <li data-testid="hero-trust-badge-secure" className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#5E7153]" /> Pay in 4 available</li>
          </motion.ul>
        </div>
        <div className="lg:col-span-5">
          <HeroImage />
        </div>
      </div>
    </section>
  );
};
